import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import useBoosterCheckout from "../../billing/useBoosterCheckout";
import { adminHref } from "../../adminPath";

/**
 * `/admin/crm/promote` (or `/t/{slug}/admin/crm/promote`, backend/practice_console.py) —
 * where a REAL promotion-run Stripe Checkout returns
 * (`backend/billing_purchases_routes.py:open_promotion_checkout`'s `success_url`/
 * `cancel_url`, built server-side: unlike the booster checkout, `POST /api/crm/
 * promotions` takes no `return_path`, so there is no "reopen the modal" here — the
 * practitioner lands on the Dashboard, where the fresh run shows in Performance's
 * "Active promotions" (`ActivePromotionsTable`, fed by `GET .../performance`).
 *
 * Reuses `useBoosterCheckout`'s return half: the SAME `?purchase=success&session_id=`
 * marker, the SAME `GET /tenant/billing/purchase/status/{sid}` (contract: the endpoint
 * answers `kind: "promotion"` exactly as it does `"booster"`/`"sku"`) — a purchase
 * outcome, not a booster subscription, but the polling+strip mechanics are identical.
 * A visit with no return marker at all (a stale bookmark, a direct hit) redirects at
 * once without a toast — there is nothing to report.
 */
export default function PromoteReturn() {
  const navigate = useNavigate();
  const t = useT("crm");

  useBoosterCheckout({
    onReturn: (data) => {
      if (data.status === "blocked") toast.error(t("promote.returnBlocked"));
      else if (data.status === "completed") toast.success(t("promote.start"));
      else if (data.status === "cancelled") toast.info(t("promote.returnCancelled"));
      navigate(adminHref("/admin/crm"), { replace: true });
    },
  });

  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get("purchase")) {
      navigate(adminHref("/admin/crm"), { replace: true });
    }
  }, [navigate]);

  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-8 h-8 animate-spin text-eh-muted" aria-hidden="true" />
    </div>
  );
}
